import { type MembershipThemeToken } from '../../themeTokens';
import type { MembershipTypeUpsertRequest } from '../../api/membership.api';

/**
 * Six day-one card themes. Mirrors the backend `CardTheme` enum.
 */
export const CREATOR_THEME_TOKENS: ReadonlyArray<MembershipThemeToken> = [
  'standard',
  'silver',
  'gold',
  'platinum',
  'emerald',
  'ruby',
] as const;

/** The membership's primary benefit engine (the card headline driver). */
export type BenefitType = 'POINTS' | 'DISCOUNT' | 'VISITS' | 'PERKS_ONLY';

/** Discount sub-mode: a percentage off, or a flat rupee amount off. */
export type DiscountMode = 'PERCENT' | 'FLAT';

/**
 * Canonical shape of the wizard's working payload. All fields are optional —
 * the wizard is filled progressively. The publish gate enforces completeness
 * via the `is*Complete` validators.
 *
 * Removed in the redesign: description, tierLevel, unlockCriteria, monthly/annual
 * split (now a single price). Added: the benefit engine + per-engine config.
 */
export type CreatorDraftPayload = {
  // Step 1 — Basics
  name?: string;
  cardTheme?: MembershipThemeToken;
  // Step 2 — Pricing (validity first, then a single price; 0 to any)
  validityDays?: number;
  pricePaise?: number;
  // Step 3 — Benefit engine
  benefitType?: BenefitType;
  // DISCOUNT engine
  discountMode?: DiscountMode;
  discountPercent?: number;
  discountFlatPaise?: number;
  // VISITS engine (pack of N sessions used within validity)
  includedVisits?: number;
  visitsServiceUuid?: string;
  // POINTS engine
  pointsEarnRatePaisePerPoint?: number;
  pointsRedeemPaisePerPoint?: number;
  pointsMinRedeem?: number;
  pointsExpiryDays?: number;
  // Step 4 — Perks (every card)
  perks?: string[];
};

export const MAX_NAME_LENGTH = 20;
export const MAX_PERKS = 5;
export const MAX_PERK_LENGTH = 24;

export const PRESET_PERKS: ReadonlyArray<string> = [
  'Priority queue skip',
  'Birthday bonus',
  'Free service / month',
  'Early booking access',
  'Partner offers',
  'Referral bonus',
  'Dedicated support',
  'Member discounts',
];

/** Validity presets (days). 0 = lifetime. */
export const VALIDITY_OPTIONS: ReadonlyArray<{ days: number; label: string }> = [
  { days: 30, label: '1 month' },
  { days: 90, label: '3 months' },
  { days: 180, label: '6 months' },
  { days: 365, label: '12 months' },
  { days: 0, label: 'Lifetime' },
];

export const BENEFIT_OPTIONS: ReadonlyArray<{
  type: BenefitType;
  label: string;
  caption: string;
}> = [
  { type: 'DISCOUNT', label: 'Member discount', caption: 'A % or ₹ off every visit' },
  { type: 'VISITS', label: 'Visit pack', caption: 'N sessions included, used within validity' },
  { type: 'POINTS', label: 'Loyalty points', caption: 'Earn on spend, redeem at checkout' },
  { type: 'PERKS_ONLY', label: 'Perks only', caption: 'No engine — the perks carry the value' },
];

const VALID_THEME_SET = new Set<string>(CREATOR_THEME_TOKENS);

export const isBasicsComplete = (payload: CreatorDraftPayload): boolean => {
  const name = String(payload.name || '').trim();
  if (name.length === 0 || name.length > MAX_NAME_LENGTH) return false;
  if (!payload.cardTheme || !VALID_THEME_SET.has(payload.cardTheme)) return false;
  return true;
};

export const isPricingComplete = (payload: CreatorDraftPayload): boolean => {
  if (payload.validityDays == null || payload.validityDays < 0) return false;
  if (payload.pricePaise == null || payload.pricePaise < 0) return false;
  return true;
};

/**
 * Benefit step completeness, per engine:
 *   - DISCOUNT: a percent (1-100) OR a flat amount (> 0), per the chosen mode;
 *   - VISITS: at least one included session;
 *   - POINTS: a positive earn rate and redeem value;
 *   - PERKS_ONLY: always complete (the perks step carries it).
 */
export const isBenefitComplete = (payload: CreatorDraftPayload): boolean => {
  switch (payload.benefitType) {
    case 'DISCOUNT': {
      if (payload.discountMode === 'FLAT') {
        return !!payload.discountFlatPaise && payload.discountFlatPaise > 0;
      }
      // default to percent
      return !!payload.discountPercent && payload.discountPercent > 0 && payload.discountPercent <= 100;
    }
    case 'VISITS':
      return !!payload.includedVisits && payload.includedVisits >= 1;
    case 'POINTS':
      return !!payload.pointsEarnRatePaisePerPoint && payload.pointsEarnRatePaisePerPoint > 0
        && !!payload.pointsRedeemPaisePerPoint && payload.pointsRedeemPaisePerPoint > 0;
    case 'PERKS_ONLY':
      return true;
    default:
      return false; // no engine picked yet
  }
};

export const isPerksComplete = (payload: CreatorDraftPayload): boolean => {
  const perks = Array.isArray(payload.perks) ? payload.perks : [];
  return perks.length > 0 && perks.length <= MAX_PERKS;
};

export const isReadyToPublish = (payload: CreatorDraftPayload): boolean =>
  isBasicsComplete(payload)
  && isPricingComplete(payload)
  && isBenefitComplete(payload)
  && isPerksComplete(payload);

export const normalizeName = (raw: string): string => raw.slice(0, MAX_NAME_LENGTH);

export const normalizePerk = (raw: string): string =>
  String(raw || '').trim().slice(0, MAX_PERK_LENGTH);

/**
 * The one-line headline the customer card shows for this engine — the same
 * language used on the mobile offering card (offering / pre-join context).
 */
export const benefitHeadline = (payload: CreatorDraftPayload): string => {
  switch (payload.benefitType) {
    case 'DISCOUNT': {
      if (payload.discountMode === 'FLAT' && payload.discountFlatPaise) {
        return `₹${Math.round(payload.discountFlatPaise / 100)} OFF every visit`;
      }
      if (payload.discountPercent) {
        return `${payload.discountPercent}% OFF everything`;
      }
      return 'Member discount';
    }
    case 'VISITS':
      return payload.includedVisits ? `${payload.includedVisits} sessions included` : 'Visit pack';
    case 'POINTS':
      return 'Earn & redeem points';
    case 'PERKS_ONLY': {
      const perks = Array.isArray(payload.perks) ? payload.perks : [];
      return perks[0] || 'Member perks';
    }
    default:
      return 'Pick a benefit';
  }
};

/**
 * Project the working payload into the `MembershipTypeUpsertRequest` the backend
 * expects. Removed fields are sent as safe defaults (description '', no tier,
 * unlock ''). Only call after `isReadyToPublish(payload)` is true.
 */
export const toUpsertRequest = (payload: CreatorDraftPayload): MembershipTypeUpsertRequest => {
  const perks = Array.isArray(payload.perks) ? payload.perks : [];
  const benefitType: BenefitType = payload.benefitType ?? 'PERKS_ONLY';
  const pointsEnabled = benefitType === 'POINTS';
  const price = payload.pricePaise ?? 0;
  const isDiscount = benefitType === 'DISCOUNT';
  const isFlat = isDiscount && payload.discountMode === 'FLAT';
  return {
    name: String(payload.name || '').trim(),
    description: '',
    themeColorToken: String(payload.cardTheme ?? 'standard'),
    // Single price; mirror into monthly so legacy issuance keeps working.
    pricePaise: price,
    monthlyPricePaise: price,
    validityDays: payload.validityDays ?? 0,
    unlockCriteria: '',
    perksJson: JSON.stringify(perks),
    benefitType,
    discountPercent: isDiscount && !isFlat ? (payload.discountPercent ?? undefined) : undefined,
    discountFlatPaise: isFlat ? (payload.discountFlatPaise ?? undefined) : undefined,
    includedVisits: benefitType === 'VISITS' ? (payload.includedVisits ?? undefined) : undefined,
    visitsServiceUuid: benefitType === 'VISITS' ? (payload.visitsServiceUuid || undefined) : undefined,
    pointsEnabled,
    pointsEarnRatePaisePerPoint: pointsEnabled ? (payload.pointsEarnRatePaisePerPoint ?? 0) : 0,
    pointsRedeemPaisePerPoint: pointsEnabled ? (payload.pointsRedeemPaisePerPoint ?? 0) : 0,
    pointsMinRedeem: payload.pointsMinRedeem ?? 100,
    pointsExpiryDays: pointsEnabled ? (payload.pointsExpiryDays ?? 365) : 0,
    isActive: true,
  };
};
